import type {
  assetCommitInputSchema,
  assetCommitOutputSchema,
  assetPresignOutputSchema,
  assetUploadAssetsInputSchema,
  mcpUploadAssetsOutputSchema,
} from "schemas/agentTools/replo/bedrockMcp";
import type {
  assetFindRequestSchema,
  assetFindResponseSchema,
  assetUpdateRequestSchema,
  assetUpdateResponseSchema,
} from "schemas/asset";
import type { z } from "zod";
import type { RequestBlobFn, RequestFn } from "./request";

export type UploadAssetsInput = z.infer<typeof assetUploadAssetsInputSchema>;
export type UploadAssetsResult = z.infer<typeof mcpUploadAssetsOutputSchema>;
export type PresignAssetsInput = {
  assets: { contentType: string; contentLength: number }[];
};
export type PresignAssetsResult = z.infer<typeof assetPresignOutputSchema>;
export type CommitAssetInput = z.infer<typeof assetCommitInputSchema>;
export type CommitAssetResult = z.infer<typeof assetCommitOutputSchema>;
export type FindAssetsInput = z.infer<typeof assetFindRequestSchema>;
export type FindAssetsResult = z.infer<typeof assetFindResponseSchema>;
export type UpdateAssetInput = z.infer<typeof assetUpdateRequestSchema>;
export type UpdateAssetResult = z.infer<typeof assetUpdateResponseSchema>;

// The blob-fetching surface only exists when a requestBlob is supplied;
// server-side callers (mini-app widget-content workers) construct requests
// from createServerBedrockRequest, which has no blob path, and must not see a
// getContent that would throw at runtime.
export function createAssetsClient(input: {
  request: RequestFn;
  requestBlob: RequestBlobFn;
}): AssetsClient & AssetsBlobClient;
export function createAssetsClient(input: { request: RequestFn }): AssetsClient;
export function createAssetsClient({
  request,
  requestBlob,
}: {
  request: RequestFn;
  requestBlob?: RequestBlobFn;
}): AssetsClient & Partial<AssetsBlobClient> {
  const client: AssetsClient & Partial<AssetsBlobClient> =
    createBaseAssetsClient({ request });
  if (requestBlob) {
    // Fetches the raw asset bytes as a Blob through the bedrock-api (same-origin
    // + authenticated), avoiding a cross-origin `fetch()` of the asset CDN whose
    // responses lack reliable CORS headers. Used by the miniapp's "copy image".
    client.getContent = (input: {
      projectId: string;
      assetId: string;
    }): Promise<Blob> => {
      return requestBlob({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/assets/${encodeURIComponent(input.assetId)}/content`,
      });
    };
  }
  return client;
}

export type AssetsClient = ReturnType<typeof createBaseAssetsClient>;

export type AssetsBlobClient = {
  getContent: (input: { projectId: string; assetId: string }) => Promise<Blob>;
};

function createBaseAssetsClient({ request }: { request: RequestFn }) {
  return {
    upload: (
      input: { projectId: string } & UploadAssetsInput,
    ): Promise<UploadAssetsResult> => {
      const { projectId, ...body } = input;
      return request<UploadAssetsResult>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/assets/upload`,
        body,
      });
    },
    // Mint presigned R2 PUT URLs so the browser uploads bytes straight to storage. Pair with commit.
    presign: (
      input: { projectId: string } & PresignAssetsInput,
    ): Promise<PresignAssetsResult> => {
      const { projectId, ...body } = input;
      return request<PresignAssetsResult>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/assets/presign`,
        body,
      });
    },
    // Record the asset row for bytes already at objectKey; images opt into server AI via generateEmbedding.
    commit: (
      input: { projectId: string } & CommitAssetInput,
    ): Promise<CommitAssetResult> => {
      const { projectId, ...body } = input;
      return request<CommitAssetResult>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/assets/commit`,
        body,
      });
    },
    find: (
      input: { projectId: string } & FindAssetsInput,
    ): Promise<FindAssetsResult> => {
      return request<FindAssetsResult>({
        method: "GET",
        path: `/api/v1/projects/${input.projectId}/assets/find`,
        query: {
          tags: input.tags,
          searchTerm: input.searchTerm,
          type: input.type,
          status: input.status,
          label: input.label,
          cursor: input.cursor,
          limit: input.limit,
          includeSummary: input.includeSummary,
        },
      });
    },
    update: (
      input: { projectId: string } & UpdateAssetInput,
    ): Promise<UpdateAssetResult> => {
      return request<UpdateAssetResult>({
        method: "PATCH",
        path: `/api/v1/projects/${input.projectId}/assets/${encodeURIComponent(input.assetId)}`,
        body: input.patch,
      });
    },
    delete: (input: {
      projectId: string;
      assetId: string;
    }): Promise<UpdateAssetResult> => {
      return request<UpdateAssetResult>({
        method: "DELETE",
        path: `/api/v1/projects/${input.projectId}/assets/${encodeURIComponent(input.assetId)}`,
      });
    },
  };
}
